import { useCallback, useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import { useBoolean, useEventListener, useInterval } from 'usehooks-ts'

import { elapsedTimeAtom } from '../state/idle-timer.atoms'

export const useIdleTimer = (onTimeExpired: () => void, isRunnning: boolean, timeout: number) => {
  const { value: suspended, setTrue: suspendInteraction, setFalse: resumeInteraction } = useBoolean(false)

  const [timeSinceLastInteraction, setTimeSinceLastInteraction] = useRecoilState(elapsedTimeAtom)

  // time remaining is not derived via a usememo, which would be nice,
  // but set in the interval, so it updates every second as output
  const [timeRemaining, setTimeRemaining] = useState<number>(timeout)

  // init the time since last interaction
  // reset it if the timer is not running anymore
  useEffect(() => {
    if (timeSinceLastInteraction === undefined && isRunnning) {
      setTimeSinceLastInteraction(Date.now())
    }
    if (!isRunnning) {
      setTimeSinceLastInteraction(undefined)
    }
  }, [timeSinceLastInteraction, setTimeSinceLastInteraction, isRunnning])

  // if the idle timeout changes, reset the timer
  useEffect(() => {
    if (timeout) {
      setTimeSinceLastInteraction(Date.now())
      setTimeRemaining(timeout)
    }
  }, [timeout, setTimeSinceLastInteraction])

  // Interval checks every second if we've gone past the timeout, to fire the callback
  useInterval(
    () => {
      if (timeSinceLastInteraction) {
        if (timeSinceLastInteraction + timeout < Date.now()) {
          setTimeRemaining(timeout)
          onTimeExpired()
          setTimeSinceLastInteraction(undefined)
        } else {
          setTimeRemaining(Math.max(timeSinceLastInteraction + timeout - Date.now(), 0))
        }
      }
    },
    isRunnning ? 1000 : null,
  )

  // Listens to user interaction to reset the timer
  const handleInteraction = useCallback(() => {
    if (!suspended) {
      setTimeSinceLastInteraction(Date.now())
    }
  }, [setTimeSinceLastInteraction, suspended])

  useEventListener('mousemove', handleInteraction)
  useEventListener('scroll', handleInteraction)
  useEventListener('keydown', handleInteraction)

  return {
    timeSinceLastInteraction,
    timeRemaining,
    suspendInteraction,
    resumeInteraction: () => {
      setTimeSinceLastInteraction(Date.now())
      resumeInteraction()
    },
  }
}
